/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/streamed-gsplat.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GraphicsBackend, GraphicsResolutionLease } from '@/graphics/GraphicsBackend'
import type {
  GSplatDebugLease,
  GSplatStreamingConfigurationLease,
  GSplatSystem,
} from '@/plugins/gsplat/GSplatSystem'
import type { GaussianSplatHandle } from '@/plugins/gsplat/types/PluginTypes'
import { getLoggerManager } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:streamed')

export type StreamedGSplatPresetName = 'ultra' | 'high' | 'medium' | 'low'

export interface StreamedGSplatPreset {
  readonly lodBaseDistance: number
  readonly lodMultiplier: number
  readonly lodRange: readonly [number, number]
}

export interface StreamedGSplatEventSource {
  subscribe(
    event: `preset:${StreamedGSplatPresetName}` | 'colorize:toggle',
    handler: () => void
  ): Disposable
}

export interface StreamedGSplatUiAdapter {
  setPreset?(preset: StreamedGSplatPresetName): void
  setColorize?(enabled: boolean): void
  updateStats?(renderedSplats: number): void
}

/** scene publication 是两阶段对象；commit 前不得出现在宿主场景图。 */
export interface StreamedGSplatScenePublication extends Disposable {
  enabled: boolean
  commit(): void
}

export interface StreamedGSplatSceneHost {
  enabled: boolean
  stageMain(handle: GaussianSplatHandle): StreamedGSplatScenePublication
  stageEnvironment(name: string, handle: GaussianSplatHandle): StreamedGSplatScenePublication
}

/** Three.js scene graph 的默认宿主适配；enabled 映射到 Object3D.visible。 */
export class ThreeStreamedGSplatSceneHost implements StreamedGSplatSceneHost {
  constructor(private readonly root: THREE.Object3D) {}

  get enabled(): boolean {
    return this.root.visible
  }

  set enabled(enabled: boolean) {
    this.root.visible = enabled
  }

  stageMain(handle: GaussianSplatHandle): StreamedGSplatScenePublication {
    const object = this.requireObject(handle)
    return this.createPublication(object, this.root)
  }

  stageEnvironment(name: string, handle: GaussianSplatHandle): StreamedGSplatScenePublication {
    const object = this.requireObject(handle)
    const child = new THREE.Group()
    child.name = name
    child.visible = false
    child.add(object)
    return this.createPublication(child, this.root)
  }

  private createPublication(
    object: THREE.Object3D,
    parent: THREE.Object3D
  ): StreamedGSplatScenePublication {
    let committed = false
    let disposed = false
    return {
      get enabled() {
        return object.visible
      },
      set enabled(enabled: boolean) {
        object.visible = enabled
      },
      commit: () => {
        if (disposed) throw new Error('StreamedGSplat scene publication 已释放')
        if (committed) return
        parent.add(object)
        committed = true
      },
      dispose: () => {
        if (disposed) return
        disposed = true
        if (object.parent === parent) parent.remove(object)
        object.removeFromParent()
      },
    }
  }

  private requireObject(handle: GaussianSplatHandle): THREE.Object3D {
    if (!handle.object3D) {
      throw new Error(`StreamedGSplat 资产 "${handle.id}" 没有可发布的 Object3D`)
    }
    return handle.object3D
  }
}

export interface StreamedGSplatOptions {
  readonly system: GSplatSystem
  readonly graphics: GraphicsBackend
  readonly scene: StreamedGSplatSceneHost
  readonly events?: StreamedGSplatEventSource
  readonly ui?: StreamedGSplatUiAdapter
  readonly splatUrl?: string
  readonly environmentUrl?: string
  readonly mobile?: boolean
  readonly devicePixelRatio?: () => number
  readonly presets?: Partial<Record<StreamedGSplatPresetName, Partial<StreamedGSplatPreset>>>
}

interface PublishedAsset {
  readonly handle: GaussianSplatHandle
  readonly publication: StreamedGSplatScenePublication
}

const DEFAULT_PRESETS: Readonly<Record<StreamedGSplatPresetName, StreamedGSplatPreset>> =
  Object.freeze({
    ultra: Object.freeze({ lodBaseDistance: 7, lodMultiplier: 3, lodRange: [0, 5] as const }),
    high: Object.freeze({ lodBaseDistance: 5, lodMultiplier: 3, lodRange: [1, 5] as const }),
    medium: Object.freeze({ lodBaseDistance: 5, lodMultiplier: 2, lodRange: [2, 5] as const }),
    low: Object.freeze({ lodBaseDistance: 5, lodMultiplier: 2, lodRange: [3, 5] as const }),
  })

const STREAMING_CONFIGURATION = Object.freeze({
  radialSorting: true,
  lodUpdateAngle: 90,
  lodBehindPenalty: 5,
  lodUpdateDistance: 1,
  lodUnderfillLimit: 10,
})

let nextControllerId = 1

function normalizePreset(
  name: StreamedGSplatPresetName,
  input?: Partial<StreamedGSplatPreset>
): StreamedGSplatPreset {
  const fallback = DEFAULT_PRESETS[name]
  const lodBaseDistance = input?.lodBaseDistance ?? fallback.lodBaseDistance
  const lodMultiplier = input?.lodMultiplier ?? fallback.lodMultiplier
  const inputRange = input?.lodRange
  const lodRange =
    inputRange &&
    inputRange.length >= 2 &&
    Number.isSafeInteger(inputRange[0]) &&
    Number.isSafeInteger(inputRange[1]) &&
    inputRange[0] >= 0 &&
    inputRange[1] >= inputRange[0]
      ? inputRange
      : inputRange === undefined
        ? fallback.lodRange
        : ([0, 5] as const)
  if (!Number.isFinite(lodBaseDistance) || lodBaseDistance <= 0) {
    throw new Error(`StreamedGSplat ${name} lodBaseDistance 非法: ${lodBaseDistance}`)
  }
  if (!Number.isFinite(lodMultiplier) || lodMultiplier <= 1) {
    throw new Error(`StreamedGSplat ${name} lodMultiplier 非法: ${lodMultiplier}`)
  }
  return Object.freeze({
    lodBaseDistance,
    lodMultiplier,
    lodRange: Object.freeze([lodRange[0], lodRange[1]]) as readonly [number, number],
  })
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

/** PlayCanvas 示例语义在正式 Unified GSplatSystem 上的 backend-neutral 编排器。 */
export class StreamedGSplat implements Disposable {
  readonly presets: Readonly<Record<StreamedGSplatPresetName, StreamedGSplatPreset>>

  private readonly owner = Symbol('StreamedGSplat')
  private readonly controllerId = nextControllerId++
  private readonly system: GSplatSystem
  private readonly graphics: GraphicsBackend
  private readonly scene: StreamedGSplatSceneHost
  private readonly events?: StreamedGSplatEventSource
  private readonly ui?: StreamedGSplatUiAdapter
  private readonly readDevicePixelRatio: () => number
  private readonly subscriptions: Disposable[] = []
  private readonly onDispose?: (controller: StreamedGSplat) => void
  private configurationLease: GSplatStreamingConfigurationLease | null = null
  private resolutionLease: GraphicsResolutionLease | null = null
  private debugLease: GSplatDebugLease | null = null
  private abortController: AbortController | null = null
  private main: PublishedAsset | null = null
  private environment: PublishedAsset | null = null
  private splatUrl: string
  private environmentUrl: string
  private generation = 0
  private currentPresetValue: StreamedGSplatPresetName
  private highResolution = false
  private colorize = false
  private initialized = false
  private disposed = false
  private readyValue: Promise<void> = Promise.resolve()

  constructor(options: StreamedGSplatOptions, onDispose?: (controller: StreamedGSplat) => void) {
    this.system = options.system
    this.graphics = options.graphics
    this.scene = options.scene
    this.events = options.events
    this.ui = options.ui
    this.splatUrl = options.splatUrl ?? ''
    this.environmentUrl = options.environmentUrl ?? ''
    this.currentPresetValue = options.mobile ? 'low' : 'medium'
    this.readDevicePixelRatio = options.devicePixelRatio ?? (() => globalThis.devicePixelRatio || 1)
    this.onDispose = onDispose
    this.presets = Object.freeze({
      ultra: normalizePreset('ultra', options.presets?.ultra),
      high: normalizePreset('high', options.presets?.high),
      medium: normalizePreset('medium', options.presets?.medium),
      low: normalizePreset('low', options.presets?.low),
    })
  }

  get currentPreset(): StreamedGSplatPresetName {
    return this.currentPresetValue
  }

  get ready(): Promise<void> {
    return this.readyValue
  }

  get mainHandle(): GaussianSplatHandle | null {
    return this.main?.handle ?? null
  }

  get environmentHandle(): GaussianSplatHandle | null {
    return this.environment?.handle ?? null
  }

  initialize(): Promise<void> {
    this.assertActive()
    if (this.initialized) return this.readyValue

    let configurationLease: GSplatStreamingConfigurationLease | null = null
    let resolutionLease: GraphicsResolutionLease | null = null
    try {
      // 两个 acquire 都只做 owner preflight；冲突时还没有配置、scene、asset 或 GPU 副作用。
      configurationLease = this.system.acquireStreamingConfigurationLease(this.owner)
      resolutionLease = this.graphics.acquireResolutionLease(this.owner)
      this.configurationLease = configurationLease
      this.resolutionLease = resolutionLease
      configurationLease.set(STREAMING_CONFIGURATION)
      this.applyResolution()
      this.subscribeEvents()
      this.initialized = true
      this.readyValue = this.reload()
      return this.readyValue
    } catch (error) {
      this.rollback(() => this.unsubscribeEvents())
      this.rollback(() => resolutionLease?.dispose())
      this.rollback(() => configurationLease?.dispose())
      this.resolutionLease = null
      this.configurationLease = null
      throw error
    }
  }

  reload(
    input: { readonly splatUrl?: string; readonly environmentUrl?: string } = {}
  ): Promise<void> {
    this.assertActive()
    if (!this.initialized) throw new Error('StreamedGSplat 尚未 initialize')
    if (input.splatUrl !== undefined) this.splatUrl = input.splatUrl
    if (input.environmentUrl !== undefined) this.environmentUrl = input.environmentUrl

    this.abortController?.abort()
    void this.readyValue.catch(() => undefined)
    const controller = new AbortController()
    this.abortController = controller
    const generation = ++this.generation
    const operations: Promise<void>[] = []

    if (this.splatUrl) {
      operations.push(this.loadMain(this.splatUrl, generation, controller.signal))
    } else {
      log.warn('[StreamedGSplat] No splatUrl provided.')
      this.disposePublished('main')
    }
    if (this.environmentUrl) {
      operations.push(this.loadEnvironment(this.environmentUrl, generation, controller.signal))
    } else {
      log.warn('[StreamedGSplat] No environmentUrl provided (skipping env child).')
      this.disposePublished('environment')
    }

    const ready = Promise.all(operations).then(() => undefined)
    this.readyValue = ready
    return ready
  }

  setPreset(preset: StreamedGSplatPresetName): void {
    this.assertActive()
    this.currentPresetValue = preset
    this.applyPresetToMain()
    this.ui?.setPreset?.(preset)
  }

  setHighResolution(enabled: boolean): void {
    this.assertActive()
    if (this.highResolution === enabled) return
    this.highResolution = enabled
    this.applyResolution()
  }

  toggleColorize(): boolean {
    this.assertActive()
    const next = !this.colorize
    const existingLease = this.debugLease
    const lease = existingLease ?? this.system.acquireDebugLease(this.owner)
    try {
      lease.set(next ? 'lod' : 'none')
    } catch (error) {
      if (!existingLease) this.rollback(() => lease.dispose())
      throw error
    }
    this.debugLease = lease
    this.colorize = next
    this.ui?.setColorize?.(next)
    return next
  }

  /** System/FrameDriver Update 调用；只读取已有统计，不请求新 frame。 */
  postUpdate(): void {
    if (!this.initialized || this.disposed) return
    this.ui?.updateStats?.(this.system.getRenderedSplatCount())
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.initialized = false
    this.generation++
    let firstError: unknown
    const step = (operation: () => void) => {
      try {
        operation()
      } catch (error) {
        firstError ??= error
      }
    }
    step(() => this.abortController?.abort())
    this.abortController = null
    step(() => this.unsubscribeEvents())
    step(() => this.disposePublished('environment'))
    step(() => this.disposePublished('main'))
    step(() => this.debugLease?.dispose())
    this.debugLease = null
    step(() => this.resolutionLease?.dispose())
    this.resolutionLease = null
    step(() => this.configurationLease?.dispose())
    this.configurationLease = null
    step(() => this.onDispose?.(this))
    if (firstError) throw firstError
  }

  private async loadMain(url: string, generation: number, signal: AbortSignal): Promise<void> {
    let handle: GaussianSplatHandle | null = null
    let publication: StreamedGSplatScenePublication | null = null
    let mounted = false
    let enabledCaptured = false
    let wasEnabled = false
    try {
      handle = await this.system.load({
        id: `streamed-gsplat-${this.controllerId}-main-${generation}`,
        name: 'MainGSplat_asset',
        url,
        signal,
      })
      this.assertCurrent(generation, signal)
      const preset = this.presets[this.currentPresetValue]
      this.system.setAssetLodConfiguration(handle.id, {
        lodBaseDistance: preset.lodBaseDistance,
        lodMultiplier: preset.lodMultiplier,
        lodRangeMin: preset.lodRange[0],
        lodRangeMax: preset.lodRange[1],
      })

      wasEnabled = this.scene.enabled
      enabledCaptured = true
      this.scene.enabled = false
      this.system.setAssetEnabled(handle.id, false)
      publication = this.scene.stageMain(handle)
      this.system.mount(handle)
      mounted = true
      publication.commit()
      this.system.setAssetEnabled(handle.id, wasEnabled)
      this.scene.enabled = wasEnabled
      enabledCaptured = false

      this.assertCurrent(generation, signal)
      const previous = this.main
      this.main = { handle, publication }
      handle = null
      publication = null
      this.disposePublishedAsset(previous)
    } catch (error) {
      if (enabledCaptured) this.rollback(() => (this.scene.enabled = wasEnabled))
      this.rollbackStaged(handle, publication, mounted)
      throw error
    }
  }

  private async loadEnvironment(
    url: string,
    generation: number,
    signal: AbortSignal
  ): Promise<void> {
    let handle: GaussianSplatHandle | null = null
    let publication: StreamedGSplatScenePublication | null = null
    let mounted = false
    try {
      handle = await this.system.load({
        id: `streamed-gsplat-${this.controllerId}-environment-${generation}`,
        name: 'EnvironmentGSplat_asset',
        url,
        signal,
      })
      this.assertCurrent(generation, signal)
      const preset = this.presets[this.currentPresetValue]
      this.system.setAssetLodConfiguration(handle.id, {
        lodBaseDistance: preset.lodBaseDistance,
        lodMultiplier: preset.lodMultiplier,
      })
      this.system.setAssetEnabled(handle.id, false)
      publication = this.scene.stageEnvironment('EnvironmentGSplat', handle)
      publication.enabled = false
      this.system.mount(handle)
      mounted = true
      publication.commit()
      publication.enabled = true
      this.system.setAssetEnabled(handle.id, true)

      this.assertCurrent(generation, signal)
      const previous = this.environment
      this.environment = { handle, publication }
      handle = null
      publication = null
      this.disposePublishedAsset(previous)
    } catch (error) {
      this.rollbackStaged(handle, publication, mounted)
      throw error
    }
  }

  private applyPresetToMain(): void {
    const handle = this.main?.handle
    if (!handle) return
    const preset = this.presets[this.currentPresetValue]
    this.system.setAssetLodConfiguration(handle.id, {
      lodBaseDistance: preset.lodBaseDistance,
      lodMultiplier: preset.lodMultiplier,
      lodRangeMin: preset.lodRange[0],
      lodRangeMax: preset.lodRange[1],
    })
  }

  private applyResolution(): void {
    const devicePixelRatio = this.readDevicePixelRatio() || 1
    const pixelRatio = this.highResolution
      ? Math.min(devicePixelRatio, 2)
      : devicePixelRatio >= 2
        ? devicePixelRatio * 0.5
        : devicePixelRatio
    this.resolutionLease?.setPixelRatio(pixelRatio)
  }

  private subscribeEvents(): void {
    if (!this.events) return
    for (const preset of ['ultra', 'high', 'medium', 'low'] as const) {
      this.subscriptions.push(
        this.events.subscribe(`preset:${preset}`, () => this.setPreset(preset))
      )
    }
    this.subscriptions.push(this.events.subscribe('colorize:toggle', () => this.toggleColorize()))
  }

  private unsubscribeEvents(): void {
    let firstError: unknown
    for (let index = this.subscriptions.length - 1; index >= 0; index--) {
      try {
        this.subscriptions[index].dispose()
      } catch (error) {
        firstError ??= error
      }
    }
    this.subscriptions.length = 0
    if (firstError) throw firstError
  }

  private disposePublished(channel: 'main' | 'environment'): void {
    const published = this[channel]
    this[channel] = null
    this.disposePublishedAsset(published)
  }

  private disposePublishedAsset(published: PublishedAsset | null): void {
    if (!published) return
    let firstError: unknown
    const step = (operation: () => void) => {
      try {
        operation()
      } catch (error) {
        firstError ??= error
      }
    }
    step(() => this.system.unmount(published.handle.id))
    step(() => published.publication.dispose())
    step(() => this.system.disposeAsset(published.handle.id))
    if (firstError) throw firstError
  }

  private rollbackStaged(
    handle: GaussianSplatHandle | null,
    publication: StreamedGSplatScenePublication | null,
    mounted: boolean
  ): void {
    if (handle && mounted) this.rollback(() => this.system.unmount(handle.id))
    this.rollback(() => publication?.dispose())
    if (handle) this.rollback(() => this.system.disposeAsset(handle.id))
  }

  private rollback(operation: () => void): void {
    try {
      operation()
    } catch {
      // rollback 不得遮蔽原始 stage/load 错误；dispose() 入口会单独汇总清理错误。
    }
  }

  private isCurrent(generation: number, signal: AbortSignal): boolean {
    return !this.disposed && !signal.aborted && generation === this.generation
  }

  private assertCurrent(generation: number, signal: AbortSignal): void {
    if (!this.isCurrent(generation, signal)) throw abortError()
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('StreamedGSplat 已销毁')
  }
}
